/* ==========================================================================
   Superboba Bundle Funnel LP — JavaScript
   Two-step bundle builder: pricing tiers → flavor selection → checkout
   ========================================================================== */

(function () {
  'use strict';

  /* ========================================================================
     STATE
     ======================================================================== */
  const state = {
    currentStep: 1,
    selectedTier: 0,         // index of selected pricing tier (default: first/best deal)
    tierQuantity: 4,         // total packs needed for this tier
    flavors: {},             // { variantId: quantity }
    totalSelected: 0,        // sum of all flavor quantities
    carouselIndex: 0,
    carouselCount: 0,
  };

  /* ========================================================================
     SUBSCRIPTION SELLING PLAN MAPPING
     1-2 bags → 30-day plan, 3-4 bags → 60-day plan, 5+ bags → longer plan
     ======================================================================== */
  function getSellingPlanId(bagCount) {
    if (bagCount <= 2) return 4573528164;
    if (bagCount <= 4) return 4573593700;
    return 4582408292;
  }

  /* ========================================================================
     DISCOUNT CODE PER QUANTITY
     Match actual totalSelected bags to the tier block with the same quantity
     (1 → tier_1, 2 → tier_2, etc.). Falls back to highest tier for 5+ bags.
     ======================================================================== */
  function getDiscountCodeForQuantity(qty) {
    var allTiers = offerSection ? offerSection.querySelectorAll('.sb-funnel__tier') : null;
    if (!allTiers || allTiers.length === 0) return null;
    var exactMatch = null;
    var highestTier = null;
    var highestQty = 0;
    allTiers.forEach(function (t) {
      var tierQty = parseInt(t.dataset.quantity) || 0;
      if (tierQty === qty) exactMatch = t;
      if (tierQty > highestQty) {
        highestQty = tierQty;
        highestTier = t;
      }
    });
    var target = exactMatch || (qty > highestQty ? highestTier : null);
    return target ? target.dataset.discountCode : null;
  }

  /* ========================================================================
     DOM REFERENCES
     ======================================================================== */
  let offerSection, step1El, step2El, step2Nav;
  let carouselImages, carouselThumbs, carouselDots, prevBtn, nextBtn;
  let tierCards, giftBoxes, giftTitles;
  let step2GiftBoxes, step2GiftTitles;
  let flavorCards;
  let stickyBar, stickyBarCta, stickyBarText;
  let step2Cta, step2CtaText, step2Counter;
  let selectFlavorsCta;

  /* ========================================================================
     STEP SWITCHING
     ======================================================================== */
  function goToStep(stepNum) {
    state.currentStep = stepNum;

    if (step1El) step1El.classList.toggle('hidden', stepNum !== 1);
    if (step2El) step2El.classList.toggle('hidden', stepNum !== 2);

    // Dispatch event for other sections to hide/show
    document.dispatchEvent(new CustomEvent('funnel:step-change', {
      detail: { step: stepNum }
    }));

    // Scroll behaviour differs per step. Step 2 is a focused flow, so pin the
    // offer section to the viewport top. Step 1 must scroll to the TRUE page top
    // (0) — scrollIntoView on the offer pins it at the top of the viewport and
    // hides the ~58px header above it (mobile top bar; desktop star-rating row
    // "Excellent 4.8 / 6,000+ happy customers").
    if (stepNum === 2) {
      if (offerSection) offerSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Lock body scroll on step 2, unlock on step 1
    document.body.classList.toggle('sb-funnel-step2-active', stepNum === 2);

    // Ensure position:fixed works on step2 nav by clearing containing block on all ancestors
    if (step2Nav && stepNum === 2) {
      var el = step2Nav.parentElement;
      while (el && el !== document.body) {
        el.style.transform = 'none';
        el.style.willChange = 'auto';
        el.style.filter = 'none';
        el.style.contain = 'none';
        el = el.parentElement;
      }
    }

    // Update step 2 counter
    if (stepNum === 2) {
      updateStep2Counter();
      updateStep2Gifts();
    }

    updateStickyBar();
  }

  /* ========================================================================
     IMAGE CAROUSEL (Step 1)
     ======================================================================== */
  function initCarousel() {
    const main = offerSection?.querySelector('.sb-funnel__carousel-main');
    if (!main) return;

    carouselImages = main.querySelectorAll('img');
    state.carouselCount = carouselImages.length;

    carouselThumbs = offerSection.querySelectorAll('.sb-funnel__carousel-thumb');
    carouselDots = offerSection.querySelectorAll('.sb-funnel__carousel-dot');
    prevBtn = offerSection.querySelector('.sb-funnel__carousel-arrow--prev');
    nextBtn = offerSection.querySelector('.sb-funnel__carousel-arrow--next');

    // Thumb clicks
    carouselThumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { setCarouselIndex(i); });
    });

    // Dot clicks
    carouselDots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { setCarouselIndex(i); });
    });

    // Arrow clicks
    if (prevBtn) prevBtn.addEventListener('click', function () { setCarouselIndex(state.carouselIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { setCarouselIndex(state.carouselIndex + 1); });

    // Touch swipe on mobile
    let startX = 0;
    main.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    main.addEventListener('touchend', function (e) {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) {
        setCarouselIndex(state.carouselIndex + (diff > 0 ? 1 : -1));
      }
    }, { passive: true });

    setCarouselIndex(0);
  }

  function setCarouselIndex(idx) {
    if (idx < 0) idx = 0;
    if (idx >= state.carouselCount) idx = state.carouselCount - 1;
    state.carouselIndex = idx;

    carouselImages.forEach(function (img, i) { img.classList.toggle('active', i === idx); });
    carouselThumbs.forEach(function (t, i) { t.classList.toggle('active', i === idx); });
    carouselDots.forEach(function (d, i) { d.classList.toggle('active', i === idx); });

    if (prevBtn) prevBtn.disabled = idx === 0;
    if (nextBtn) nextBtn.disabled = idx === state.carouselCount - 1;
  }

  /* ========================================================================
     PRICING TIER SELECTION (Step 1)
     ======================================================================== */
  function initTiers() {
    tierCards = offerSection?.querySelectorAll('.sb-funnel__tier:not(.sb-funnel__tier--soldout)');
    if (!tierCards) return;

    tierCards.forEach(function (card, i) {
      card.addEventListener('click', function () {
        // In theme editor, defer DOM mutations so Shopify's block selection runs first
        if (window.Shopify && window.Shopify.designMode) {
          setTimeout(function () { selectTier(card); }, 0);
        } else {
          selectTier(card);
        }
      });
    });

    // Select the configured default tier (from `data-default-tier-qty` on the
    // section root, set via theme editor). Falls back to the first non-soldout
    // tier if the configured qty doesn't match any tier or is sold out.
    var defaultQty = parseInt(offerSection.dataset.defaultTierQty) || 0;
    var defaultTier = null;
    if (defaultQty > 0) {
      tierCards.forEach(function (card) {
        if (!defaultTier && parseInt(card.dataset.quantity) === defaultQty) {
          defaultTier = card;
        }
      });
    }
    var fallbackTier = offerSection.querySelector('.sb-funnel__tier:not(.sb-funnel__tier--soldout)');
    var initialTier = defaultTier || fallbackTier;
    if (initialTier) selectTier(initialTier);
  }

  function selectTier(card) {
    var allTiers = offerSection.querySelectorAll('.sb-funnel__tier');
    allTiers.forEach(function (t) { t.classList.remove('sb-funnel__tier--selected'); });
    card.classList.add('sb-funnel__tier--selected');

    state.tierQuantity = parseInt(card.dataset.quantity) || 1;
    state.selectedTier = parseInt(card.dataset.tierIndex) || 0;

    // Update dynamic CTA details based on selected tier
    var frequency = card.dataset.frequency || '';
    var discount = card.dataset.discount || '';
    var salePrice = card.dataset.salePrice || '';
    var originalTotal = card.dataset.originalTotal || '';
    var qty = state.tierQuantity;

    // Update "Subscribe & Save" label
    var subLabel = offerSection.querySelector('.sb-funnel__sub-label');
    if (subLabel && frequency && discount) {
      subLabel.textContent = 'Subscribe & Save - ' + frequency + ' - ' + discount + ' First Order*';
    }

    // Update CTA button prices
    var priceOriginal = offerSection.querySelector('[data-step="1"] .sb-funnel__cta-price-original');
    var priceSale = offerSection.querySelector('[data-step="1"] .sb-funnel__cta-price-sale');
    if (priceOriginal && originalTotal) priceOriginal.textContent = originalTotal;
    if (priceSale && salePrice) priceSale.textContent = salePrice;

    // Update subscription benefit line — ship cadence based on tier bag count
    var benefitLine = offerSection.querySelector('.sb-funnel__sub-benefits-item');
    if (benefitLine) {
      var shipDays = qty <= 2 ? 30 : 60;
      benefitLine.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>' +
        'Your bundle ships in ' + shipDays + ' days';
    }

    updateStep1Gifts();
    updateStickyBar();
  }

  /* ========================================================================
     GIFT TIER PROGRESS
     ======================================================================== */
  function initGifts() {
    giftBoxes = offerSection?.querySelectorAll('[data-step="1"] .sb-funnel__gift');
    giftTitles = offerSection?.querySelectorAll('[data-step="1"] .sb-funnel__gift-title');
    step2GiftBoxes = offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift');
    step2GiftTitles = offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift-title');
  }

  function updateStep1Gifts() {
    if (!giftBoxes) return;
    giftBoxes.forEach(function (box, i) {
      var threshold = parseInt(box.dataset.unlockAt) || 1;
      var isUnlocked = state.tierQuantity >= threshold;
      box.classList.toggle('sb-funnel__gift--unlocked', isUnlocked);
    });
    if (giftTitles) {
      giftTitles.forEach(function (title, i) {
        var box = giftBoxes[i];
        if (!box) return;
        var threshold = parseInt(box.dataset.unlockAt) || 1;
        title.classList.toggle('sb-funnel__gift-title--active', state.tierQuantity >= threshold);
      });
    }
  }

  function updateStep2Gifts() {
    if (!step2GiftBoxes) return;
    step2GiftBoxes.forEach(function (box, i) {
      var threshold = parseInt(box.dataset.unlockAt) || 1;
      var isUnlocked = state.totalSelected >= threshold;
      box.classList.toggle('sb-funnel__gift--unlocked', isUnlocked);
    });
    if (step2GiftTitles) {
      step2GiftTitles.forEach(function (title, i) {
        var box = step2GiftBoxes[i];
        if (!box) return;
        var threshold = parseInt(box.dataset.unlockAt) || 1;
        title.classList.toggle('sb-funnel__gift-title--active', state.totalSelected >= threshold);
      });
    }
  }

  /* ========================================================================
     FLAVOR QUANTITY CONTROLS (Step 2)
     ======================================================================== */
  function initFlavors() {
    flavorCards = offerSection?.querySelectorAll('.sb-funnel__flavor');
    if (!flavorCards) return;

    flavorCards.forEach(function (card) {
      var variantId = card.dataset.variantId;
      var addBtn = card.querySelector('.sb-funnel__add-btn');
      var qtyBox = card.querySelector('.sb-funnel__qty-box');
      var qtyDisplay = card.querySelector('.sb-funnel__qty-display');
      var plusBtn = card.querySelector('.sb-funnel__qty-plus');
      var minusBtn = card.querySelector('.sb-funnel__qty-minus');

      if (addBtn) {
        addBtn.addEventListener('click', function () {
          state.flavors[variantId] = 1;
          addBtn.style.display = 'none';
          if (qtyBox) qtyBox.classList.add('visible');
          if (qtyDisplay) qtyDisplay.textContent = '1';
          card.classList.add('sb-funnel__flavor--active');
          recalcTotal();
        });
      }

      if (plusBtn) {
        plusBtn.addEventListener('click', function () {
          state.flavors[variantId] = (state.flavors[variantId] || 0) + 1;
          if (qtyDisplay) qtyDisplay.textContent = state.flavors[variantId];
          recalcTotal();
        });
      }

      if (minusBtn) {
        minusBtn.addEventListener('click', function () {
          state.flavors[variantId] = (state.flavors[variantId] || 0) - 1;
          if (state.flavors[variantId] <= 0) {
            delete state.flavors[variantId];
            // Reset to Add+ button
            if (addBtn) addBtn.style.display = '';
            if (qtyBox) qtyBox.classList.remove('visible');
            card.classList.remove('sb-funnel__flavor--active');
          } else {
            if (qtyDisplay) qtyDisplay.textContent = state.flavors[variantId];
          }
          recalcTotal();
        });
      }
    });
  }

  function recalcTotal() {
    state.totalSelected = 0;
    for (var key in state.flavors) {
      state.totalSelected += state.flavors[key];
    }
    updateStep2Gifts();
    updateStep2Counter();
    updateStep2AtcInfo();
    updateStickyBar();
  }

  /* ========================================================================
     STEP 2 COUNTER + CTA
     ======================================================================== */
  function updateStep2Counter() {
    if (!step2Counter) return;
    step2Counter.textContent = 'Select at least ' + state.tierQuantity + ' bag' + (state.tierQuantity === 1 ? '' : 's');

    var remaining = state.tierQuantity - state.totalSelected;

    if (step2Cta) {
      if (remaining > 0) {
        step2Cta.disabled = true;
        step2Cta.classList.remove('sb-funnel__cta--ready');
        if (step2CtaText) step2CtaText.textContent = 'Select ' + remaining + ' more bag' + (remaining > 1 ? 's' : '');
      } else {
        step2Cta.disabled = false;
        step2Cta.classList.add('sb-funnel__cta--ready');
        step2Cta.classList.remove('sb-funnel__cta--loading');
        step2Cta.dataset.processing = 'false';
        if (step2CtaText) step2CtaText.textContent = 'Go to Checkout';
      }
    }
  }

  /* ========================================================================
     STEP 2 ATC INFO (savings + gifts unlocked)
     ======================================================================== */
  function updateStep2AtcInfo() {
    var savingEl = offerSection?.querySelector('[data-saving]') || document.querySelector('[data-saving]');
    var giftsEl = offerSection?.querySelector('[data-gifts]') || document.querySelector('[data-gifts]');

    // Calculate savings % from selected tier
    if (savingEl) {
      var selectedTier = offerSection?.querySelector('.sb-funnel__tier--selected');
      var discount = selectedTier?.dataset.discount || '0%';
      var pct = discount.replace(/[^0-9]/g, '') || '0';
      savingEl.textContent = state.totalSelected > 0 ? "You're saving " + pct + '%' : "You're saving 0%";
    }

    // Count unlocked gifts
    if (giftsEl) {
      var allGifts = offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift') || [];
      var totalGifts = allGifts.length;
      var unlocked = 0;
      allGifts.forEach(function (box) {
        var threshold = parseInt(box.dataset.unlockAt) || 1;
        if (state.totalSelected >= threshold) unlocked++;
      });
      giftsEl.textContent = '✨ GIFTS UNLOCKED: ' + unlocked + '/' + totalGifts;
    }
  }

  /* ========================================================================
     STICKY BOTTOM BAR
     ======================================================================== */
  function updateStickyBar() {
    if (!stickyBar) return;

    if (state.currentStep === 1) {
      // Visibility is controlled by scroll observer, just update text/state
      if (stickyBarText) stickyBarText.textContent = 'Select Flavors';
      if (stickyBarCta) {
        stickyBarCta.disabled = false;
        stickyBarCta.classList.remove('sb-funnel__cta--ready');
      }
    } else {
      // Hide sticky bar in step 2 (step 2 has its own sticky ATC)
      stickyBar.style.display = 'none';
    }
  }

  function initStickyBarObserver() {
    if (!stickyBar || !selectFlavorsCta) return;

    // Hide sticky bar by default
    stickyBar.style.display = 'none';

    // Show sticky bar only when the main "Select Flavors" CTA scrolls out of view
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (state.currentStep !== 1) return;
        if (entry.isIntersecting) {
          // Main CTA is visible — hide sticky bar
          stickyBar.style.display = 'none';
        } else {
          // Main CTA scrolled out of view — show sticky bar
          stickyBar.style.display = '';
        }
      });
    }, { threshold: 0 });

    observer.observe(selectFlavorsCta);
  }

  /* ========================================================================
     DEBUG STATUS PANEL (visible on mobile when ?debug=1 is in URL)
     ======================================================================== */
  function isDebug() {
    try { return new URLSearchParams(window.location.search).get('debug') === '1'; }
    catch (e) { return false; }
  }
  function debugStatus(msg, isError) {
    if (!isDebug()) return;
    var el = document.getElementById('sb-funnel-debug');
    if (!el) {
      el = document.createElement('div');
      el.id = 'sb-funnel-debug';
      el.style.cssText = 'position:fixed;bottom:0;left:0;right:0;background:#111;color:#fff;font:12px/1.3 monospace;padding:8px 12px;z-index:999999;max-height:40vh;overflow:auto;white-space:pre-wrap;word-break:break-all';
      document.body.appendChild(el);
    }
    var line = document.createElement('div');
    line.style.color = isError ? '#ff6b6b' : '#b2f5b2';
    line.textContent = new Date().toLocaleTimeString() + ' ' + msg;
    el.appendChild(line);
    el.scrollTop = el.scrollHeight;
  }

  /* ========================================================================
     CART INTEGRATION
     ======================================================================== */
  function addToCart() {
    var flavorItems = [];
    var sellingPlanId = getSellingPlanId(state.totalSelected);
    for (var variantId in state.flavors) {
      if (state.flavors[variantId] > 0) {
        flavorItems.push({
          id: parseInt(variantId),
          quantity: state.flavors[variantId],
          selling_plan: sellingPlanId
        });
      }
    }

    if (flavorItems.length === 0) {
      debugStatus('addToCart: 0 flavor items, aborting', true);
      return;
    }
    debugStatus('addToCart start: ' + flavorItems.length + ' flavor items, sellingPlan=' + sellingPlanId);

    // Collect unlocked gift items separately — they're best-effort.
    // If a gift is sold out, Shopify 422s the whole /cart/add.js. We add
    // flavors first (required), then each gift individually so a sold-out
    // gift silently falls off instead of blocking checkout.
    var giftItems = [];
    var giftBoxes = offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift') || [];
    giftBoxes.forEach(function (box) {
      var threshold = parseInt(box.dataset.unlockAt) || 1;
      var giftVariant = box.dataset.giftVariant;
      if (giftVariant && state.totalSelected >= threshold) {
        giftItems.push({
          id: parseInt(giftVariant),
          quantity: 1,
          properties: { '_gift': 'true' }
        });
      }
    });
    debugStatus('payload: ' + flavorItems.length + ' flavors + ' + giftItems.length + ' gifts (gifts best-effort)');

    // Disable button during request. Drop --ready so its ::before/::after
    // (shimmer/glow) don't fight the --loading spinner ::after.
    if (step2Cta) {
      step2Cta.disabled = true;
      step2Cta.classList.remove('sb-funnel__cta--ready');
      step2Cta.classList.add('sb-funnel__cta--loading');
    }
    function setStageText(txt) {
      if (step2CtaText) step2CtaText.textContent = txt;
    }
    setStageText('Building your order...');

    // ---- Checkout timing instrumentation -------------------------------
    // Measures each phase of the add->checkout sequence and counts /cart/add
    // 429 retries (Shopify rate-limit / "Verifying your connection" challenge,
    // which trigger the 1200ms backoff below). Always logged to console; also
    // stashed in sessionStorage so it survives the navigation to /checkout —
    // read it there with: JSON.parse(sessionStorage.getItem('sb-funnel-timing'))
    var _t = { start: performance.now(), addRetries: 0, phases: {} };
    function _mark(k) { _t.phases[k] = Math.round(performance.now() - _t.start); }
    function _emitTiming(extra) {
      var payload = {
        slug: funnelSlug,
        phases: _t.phases,
        addRetries: _t.addRetries,
        codes: extra && extra.codes != null ? extra.codes : undefined,
        path: extra && extra.path,
        totalMs: Math.round(performance.now() - _t.start)
      };
      try { console.log('[sb-funnel][timing]', JSON.stringify(payload)); } catch (e) {}
      try { sessionStorage.setItem('sb-funnel-timing', JSON.stringify(payload)); } catch (e) {}
      debugStatus('timing: ' + payload.totalMs + 'ms (addRetries=' + payload.addRetries + ', codes=' + payload.codes + ')');
    }

    // Safety timeout: if nothing navigates in 15s (slow mobile networks,
    // failed redirects), re-enable the button so the user isn't stuck.
    var recoveryTimeout = setTimeout(function () {
      if (step2Cta) {
        step2Cta.disabled = false;
        step2Cta.classList.remove('sb-funnel__cta--loading');
        step2Cta.dataset.processing = 'false';
      }
    }, 15000);
    function clearRecovery() { clearTimeout(recoveryTimeout); }
    function resetCtaState() {
      clearRecovery();
      if (step2Cta) {
        step2Cta.disabled = false;
        step2Cta.classList.remove('sb-funnel__cta--loading');
        step2Cta.dataset.processing = 'false';
      }
    }

    // /cart/add.js can return 429 — either third-party cart scripts hammering
    // /cart.json, Shopify rate-limiting a request burst, or Shopify's
    // "Verifying your connection" bot challenge. A transient 429 usually clears
    // within a few hundred ms, so retry with EXPONENTIAL BACKOFF + JITTER rather
    // than one long flat wait: ~400ms → ~800ms → ~1600ms (up to 3 retries).
    // This recovers ~800ms faster than the old flat 1200ms in the common case
    // (first retry succeeds) and gives more chances before checkout hard-fails.
    // Jitter de-syncs concurrent retries so we don't re-trip the limit in lockstep.
    var ADD_MAX_ATTEMPTS = 4; // 1 initial attempt + 3 retries
    function addBatch(items, attempt) {
      attempt = attempt || 1;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        if (res.ok) return res.json();
        if (res.status === 429 && attempt < ADD_MAX_ATTEMPTS) {
          _t.addRetries++;
          var base = 400 * Math.pow(2, attempt - 1);           // 400, 800, 1600
          var delay = Math.round(base * (0.75 + Math.random() * 0.5)); // ±25% jitter
          debugStatus('429 on /cart/add — retry ' + attempt + '/' + (ADD_MAX_ATTEMPTS - 1) + ' in ' + delay + 'ms', true);
          return new Promise(function (resolve) {
            setTimeout(function () { resolve(addBatch(items, attempt + 1)); }, delay);
          });
        }
        return res.text().then(function (body) {
          var err = new Error('HTTP ' + res.status + ': ' + body.slice(0, 200));
          err.status = res.status;
          err.body = body;
          throw err;
        });
      });
    }
    // Fast path: try flavors + gifts in ONE cart/add.js call (1 round-trip).
    // If Shopify rejects (usually because a gift is sold out — 422), fall back
    // to flavors alone + individual parallel gift adds.
    function addAllItems() {
      var combined = flavorItems.concat(giftItems);
      debugStatus('POST /cart/add.js (combined: ' + combined.length + ' items)');
      return addBatch(combined, 1).then(function (data) {
        debugStatus('combined add OK (1 round-trip)');
        return data;
      }).catch(function (err) {
        debugStatus('combined add failed, fallback: ' + (err.message || '').slice(0, 80), true);
        // Flavors required, gifts best-effort in parallel.
        return addBatch(flavorItems, 1).then(function (data) {
          debugStatus('flavors OK');
          if (giftItems.length === 0) return data;
          return Promise.all(giftItems.map(function (gift) {
            return addBatch([gift], 1).then(function () {
              debugStatus('gift added: ' + gift.id);
            }).catch(function (e) {
              debugStatus('gift skipped (' + gift.id + '): ' + (e.message || '').slice(0, 80), true);
            });
          })).then(function () { return data; });
        });
      });
    }

    // Cart attributes for order tagging via Shopify Flow.
    // Flow rule: if cart.attributes["multi-step-lp"] exists, tag order with
    // `lander:<slug>` and `tier:<qty>`.
    var funnelSlug = offerSection && offerSection.dataset.funnelSlug
      ? offerSection.dataset.funnelSlug
      : 'mt-ms-01';
    var offerSource = offerSection && offerSection.dataset.offerSource
      ? offerSection.dataset.offerSource
      : 'paid-offer-bundle';
    function setCartAttributes() {
      var attrs = {
        tier: String(state.totalSelected),
        source: offerSource
      };
      attrs['multi-step-lp'] = funnelSlug;
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).catch(function () { /* non-fatal */ });
    }

    // Shared discount-code collection: tier code (picked by state.totalSelected
    // so adding bags auto-bumps tier), then per-gift unlock codes, then the
    // global gift discount. Dedupes via `seen`.
    function collectCodes() {
      var codes = [];
      var seen = {};
      function pushCode(c) {
        if (!c) return;
        var trimmed = c.trim();
        if (!trimmed || seen[trimmed]) return;
        seen[trimmed] = true;
        codes.push(trimmed);
      }
      pushCode(getDiscountCodeForQuantity(state.totalSelected));
      var step2Gifts = offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift') || [];
      step2Gifts.forEach(function (box) {
        var threshold = parseInt(box.dataset.unlockAt) || 1;
        if (state.totalSelected >= threshold) pushCode(box.dataset.giftDiscountCode);
      });
      pushCode(offerSection?.dataset.giftDiscount);
      return codes;
    }

    function handleCheckoutError(err) {
      console.error('Cart add failed:', err);
      debugStatus('EXCEPTION: ' + (err && err.message ? err.message : String(err)), true);
      resetCtaState();
    }

    // Checkout flow:
    //   1. /cart/clear.js
    //   2. /cart/add.js + /cart/update.js (attributes) — IN PARALLEL (saves 1 RTT)
    //   3. Pre-fetch all-but-last discount codes via /discount/CODE individually
    //      (each call saves the code to the cart session). Final redirect via
    //      /discount/LAST_CODE?redirect=/checkout applies the last one and goes
    //      to checkout. This is the only reliable way to stack codes in Shopify
    //      — `/discount/CODE1,CODE2` is NOT supported (Shopify treats the whole
    //      comma-string as one code lookup, so NO discount applies).
    debugStatus('checkout: ' + funnelSlug);
    console.log('[sb-funnel] checkout flow start, slug=' + funnelSlug);
    fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
    .catch(function () { /* non-fatal; continue to add */ })
    .then(function () {
      _mark('clear');
      setStageText('Building your order...');
      // cart/add + cart/update in parallel — saves one round-trip vs sequential.
      return Promise.all([addAllItems(), setCartAttributes()]);
    })
    .then(function () {
      _mark('addUpdate');
      setStageText('Activating your savings...');
      var codes = collectCodes();
      console.log('[sb-funnel] codes:', codes, 'totalSelected=' + state.totalSelected);

      if (codes.length === 0) {
        debugStatus('no codes → /checkout');
        console.log('[sb-funnel] → /checkout (no codes)');
        _emitTiming({ codes: 0, path: 'no-codes' });
        clearRecovery();
        window.location.href = '/checkout';
        return;
      }

      // Apply ALL codes in a SINGLE navigation via the /checkout?discount= query
      // param (comma-separated). This is what high-velocity offer pages (e.g.
      // mycarpe.com) do, and it replaces the old per-code /discount/CODE prefetch
      // chain — removing a full round-trip AND shrinking the request burst, which
      // also lowers the chance of tripping Shopify's edge bot challenge on /cart.
      //
      // IMPORTANT: the comma syntax only works on the ?discount= QUERY param, NOT
      // the /discount/CODE1,CODE2 PATH form (Shopify treats that whole string as
      // one code lookup). Stacking requires the codes be configured combinable —
      // verified via Admin API: tier codes + FREEGIFT all combinesWith
      // order/product/shipping = true.
      clearRecovery();
      var nav = '/checkout?discount=' + codes.map(encodeURIComponent).join(',');
      debugStatus('navigate → ' + nav);
      console.log('[sb-funnel] navigate →', nav);
      _emitTiming({ codes: codes.length, path: 'checkout-discount-param' });
      window.location.href = nav;
    })
    .catch(handleCheckoutError);
  }

  /* ========================================================================
     CONTENT SECTION VISIBILITY
     ======================================================================== */
  document.addEventListener('funnel:step-change', function (e) {
    // Hide all sections between the offer and sticky bar when on step 2
    // Target by section class names AND data attribute
    var selectors = [
      '.sb-lp-funnel-benefits-section',
      '.sb-lp-funnel-social-section',
      '.sb-lp-funnel-sticky-bar-section',
      '[data-funnel-content]'
    ];

    // Also find any shopify-section that wraps our reused sections
    var allSections = document.querySelectorAll('.shopify-section');
    allSections.forEach(function (section) {
      // Skip the offer section itself
      if (section.querySelector('#sb-funnel-offer')) return;
      // Skip if it's the sticky bar
      if (section.querySelector('.sb-funnel-sticky')) {
        if (e.detail.step === 2) {
          section.classList.add('sb-funnel-hidden');
        } else {
          section.classList.remove('sb-funnel-hidden');
        }
        return;
      }
      // Check if this section is between offer and sticky
      var offerEl = document.getElementById('sb-funnel-offer');
      if (!offerEl) return;
      var offerSectionEl = offerEl.closest('.shopify-section');
      if (!offerSectionEl) return;

      // Compare DOM order
      if (offerSectionEl.compareDocumentPosition(section) & Node.DOCUMENT_POSITION_FOLLOWING) {
        if (e.detail.step === 2) {
          section.classList.add('sb-funnel-hidden');
        } else {
          section.classList.remove('sb-funnel-hidden');
        }
      }
    });
  });

  /* ========================================================================
     COUNTDOWN TIMER
     ======================================================================== */
  function initCountdown() {
    var timerEl = document.querySelector('[data-funnel-countdown]');
    if (!timerEl) return;

    var endStr = timerEl.dataset.funnelCountdown;
    var endDate;

    if (endStr) {
      endDate = new Date(endStr);
    } else {
      // Rolling 3-day countdown
      endDate = new Date();
      endDate.setDate(endDate.getDate() + 3);
      endDate.setHours(23, 59, 59, 0);
    }

    var hrsEl = timerEl.querySelector('[data-hours]');
    var minEl = timerEl.querySelector('[data-minutes]');
    var secEl = timerEl.querySelector('[data-seconds]');

    function tick() {
      var now = Date.now();
      var diff = endDate.getTime() - now;
      if (diff <= 0) diff = 0;

      var hrs = Math.floor(diff / 3600000);
      var mins = Math.floor((diff % 3600000) / 60000);
      var secs = Math.floor((diff % 60000) / 1000);

      if (hrsEl) hrsEl.textContent = String(hrs).padStart(2, '0');
      if (minEl) minEl.textContent = String(mins).padStart(2, '0');
      if (secEl) secEl.textContent = String(secs).padStart(2, '0');
    }

    tick();
    setInterval(tick, 1000);
  }

  /* ========================================================================
     INIT
     ======================================================================== */
  function init() {
    offerSection = document.getElementById('sb-funnel-offer');
    if (!offerSection) return;

    step1El = offerSection.querySelector('[data-step="1"]');
    step2El = offerSection.querySelector('[data-step="2"]');

    // Step 1 elements
    selectFlavorsCta = offerSection.querySelector('[data-action="select-flavors"]');
    if (selectFlavorsCta) {
      selectFlavorsCta.addEventListener('click', function () { goToStep(2); });
    }

    // Step 2 elements
    step2Nav = offerSection.querySelector('.sb-funnel__step2-nav');
    var backBtn = offerSection.querySelector('.sb-funnel__back-btn');
    if (backBtn) {
      backBtn.addEventListener('click', function () { goToStep(1); });
    }

    step2Cta = offerSection.querySelector('[data-action="checkout"]');
    step2CtaText = step2Cta?.querySelector('.sb-funnel__cta-text');
    step2Counter = offerSection.querySelector('.sb-funnel__step2-title');

    if (step2Cta) {
      step2Cta.addEventListener('click', function () {
        if (step2Cta.disabled) return;
        // Prevent double-submit from rapid taps on mobile
        if (step2Cta.dataset.processing === 'true') return;
        step2Cta.dataset.processing = 'true';
        addToCart();
      });
    }

    // Sticky bar
    stickyBar = document.querySelector('.sb-funnel-sticky');
    stickyBarCta = stickyBar?.querySelector('.sb-funnel__cta');
    stickyBarText = stickyBar?.querySelector('.sb-funnel__cta-text');

    if (stickyBarCta) {
      stickyBarCta.addEventListener('click', function () {
        if (state.currentStep !== 1) return;
        // Scroll back to the quantity selector so the user can review tiers,
        // then hit the main CTA to advance.
        var tiersEl = offerSection?.querySelector('.sb-funnel__tiers');
        (tiersEl || offerSection)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    // Initialize components
    initCarousel();
    initGifts();
    initTiers();
    initFlavors();
    initCountdown();
    initStickyBarObserver();
    updateStickyBar();
  }

  /* ========================================================================
     RESET ON RETURN — if the user hits back from checkout, reset to step 1
     with no flavors selected so they can't accidentally reuse stale state
     ======================================================================== */
  function resetFunnelState() {
    state.flavors = {};
    state.totalSelected = 0;
    if (flavorCards) {
      flavorCards.forEach(function (card) {
        card.classList.remove('sb-funnel__flavor--active');
        var addBtn = card.querySelector('.sb-funnel__add-btn');
        var qtyBox = card.querySelector('.sb-funnel__qty-box');
        var qtyDisplay = card.querySelector('.sb-funnel__qty-display');
        if (addBtn) addBtn.style.display = '';
        if (qtyBox) qtyBox.classList.remove('visible');
        if (qtyDisplay) qtyDisplay.textContent = '1';
      });
    }
    if (step2Cta) {
      step2Cta.disabled = false;
      step2Cta.classList.remove('sb-funnel__cta--loading');
      step2Cta.dataset.processing = 'false';
    }
    goToStep(1);
  }

  // Reset ONLY on bfcache restore (event.persisted === true) — i.e. the user
  // hit browser-back from checkout and the page was restored from cache showing
  // stale step-2 state. Do NOT reset on a fresh load: pageshow fires AFTER the
  // `load` event, so on a heavy LP it can fire 1-2s in — and if the visitor
  // already tapped "Select Flavors" during that window, an unconditional reset
  // bounces them back to step 1. A fresh load already starts on step 1 anyway.
  window.addEventListener('pageshow', function (event) {
    if (!offerSection) return;
    if (event.persisted) resetFunnelState();
  });

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
